import { useEffect, useRef } from 'react';

import { useDutchieIntelligenceContext } from '../../context/dutchie-intelligence';

import { registerIntelligenceContextCallback, unregisterIntelligenceContextCallback } from './context-registration';

/**
 * Bridge component that connects DutchieIntelligenceContext to the React Query callback.
 *
 * This component must be rendered inside DutchieIntelligenceProvider so it can access the context,
 * and it registers the callback that allows React Query to automatically register
 * query results as AI context.
 *
 * Uses a ref to prevent re-registration on every render, avoiding memory leaks.
 */
export function ReactQueryResource() {
  const intelligenceContext = useDutchieIntelligenceContext();
  const intelligenceContextRef = useRef(intelligenceContext);

  // Update ref when intelligenceContext changes (shouldn't happen often)
  useEffect(() => {
    intelligenceContextRef.current = intelligenceContext;
  }, [intelligenceContext]);

  useEffect(() => {
    // Register callback once on mount, using ref for stable access
    registerIntelligenceContextCallback({
      setComponentContext: (...args) => intelligenceContextRef.current.setComponentContext(...args),
    });

    // Unregister the callback when the component unmounts
    return () => {
      unregisterIntelligenceContextCallback();
    };
    // Empty deps - register only once on mount
  }, []);

  // This component doesn't render anything
  return null;
}
